import { uploadsService } from '@/shared/api/services/uploads.service';
import {
  findBestForMaxArea,
  ASPECT_RATIOS,
} from '@/shared/constants/aspect-ratios';
import type { ProcessedFile } from '@/shared/lib/services/image-upload.service';
import type { CreateUploadAttributes } from '@/types/upload-intake';
import type { UploadPolicy } from '@/types/project-reference';

/** Where a VIEW would land if the server decides the file is one. */
export type AngleIntakePlacement = Pick<
  CreateUploadAttributes,
  'intent' | 'concept_id' | 'angle_type' | 'replace_concept_angle_id'
>;

/**
 * One intake per file: register it against the building and get a presigned
 * PUT back. Where a VIEW would land (this concept, this slot, replacing this
 * one) travels with the registration; whether the file IS a view is the
 * server's answer, delivered later on the intake itself (ADR-002).
 *
 * Shared by every photo entry point (the upload window and the first-run
 * wizard) so the registration payload cannot drift between them.
 */
export async function registerAngleIntake(
  projectId: string,
  file: ProcessedFile,
  placement: AngleIntakePlacement = {}
): Promise<{ id: string; uploadPolicy: UploadPolicy }> {
  const aspectRatioKey = findBestForMaxArea(file.width, file.height);
  const aspectRatio = ASPECT_RATIOS[aspectRatioKey].geminiValue;

  const upload = await uploadsService.create(projectId, {
    md5: file.md5,
    file_name: file.file.name,
    content_type: file.file.type,
    width: file.width,
    height: file.height,
    aspect_ratio: aspectRatio,
    ...placement,
  });

  const uploadPolicy = upload.attributes.upload_policy;
  if (!uploadPolicy) {
    throw new Error('Intake registered without an upload policy');
  }

  return { id: upload.id, uploadPolicy };
}
